import { log } from '@/log'
import { ApiCreditcard } from '@/models/api/creditcard'
import {
  CreditcardRepository,
  FrontAPICreditCardRepository,
  RegisterContext,
} from '@/models/payment/creditcard/repository/creditcard-repository'
import {
  CreditcardServiceForSubscription,
  isCreateForSubscriptionError,
  ThreeDSecureCreditcardRegisterResult,
} from '@/services/api/subscription/payment/creditcard-service-for-subscription'
import { CreditcardPaymentProviderServiceForSubscription } from '@/services/payment/creditcard-payment-provider/creditcard-payment-provider-service'
import { trimCDATA } from '@/util/cdata'
import { CreditcardFormState } from '@spa/components/organisms/payment/creditcard/creditcardFormState'
import { User } from '@spa/store/modules/user/types'
import { identity } from 'fp-ts/lib/function'
import { none, Option, some } from 'fp-ts/lib/Option'
import { ActionRequest } from '../../action/action'
import {
  CreditcardAuthenticationError,
  DefaultThreeDSecureRegistrationError,
  errorOnThreeDSecureRegistration,
  LoadingCreditcardSciptError,
  ThreeDAuthHtmlParseError,
} from '../../repository/errors'

export interface CreditcardForSubscriptionRepository extends CreditcardRepository {
  /**
   * サブスク機能では、一度に一つのクレジットカードしか登録できない使用である前提.
   * @see https://github.com/my-color/front/issues/5817
   */
  findRegistered(): Promise<Option<ApiCreditcard>>
}

export class FrontApiRepositoryForSubscription
  extends FrontAPICreditCardRepository<
      CreditcardServiceForSubscription,
      CreditcardPaymentProviderServiceForSubscription
    >
  implements CreditcardForSubscriptionRepository {

  async findRegistered(): Promise<Option<ApiCreditcard>> {
    const result = await this.creditCardService.list()

    return result.fold(
      (error) => {
        throw error
      },
      (creditcardList: ApiCreditcard[]) => {

        // 有料会員登録機能ではクレカは一つしか登録できない仕様のため、
        // 一覧取得の要素数は 0 か 1 という前提
        return creditcardList.length > 0
               ? some(creditcardList[0])
               : none
      }
    )
  }

  async register(
    creditcard: CreditcardFormState,
    user: User,
    context: RegisterContext = { threeDSecureRedirectUrl: none, threeDSecureExtraProfile: none }
  ): Promise<ActionRequest> {
    {
      await this.scriptLoading.catch((e: Error) => {
        throw new LoadingCreditcardSciptError(e.message)
      })

      // スクリプトの取得が終わったことを確定させてから、後続処理を実行
      const paymentResult = await this.cardPaymentProvider.createRegisterCardPayload(
        creditcard,
        user
      )

      const token = paymentResult.fold(
        (e) => {
          throw new CreditcardAuthenticationError(e.message)
        },
        identity
      )

      if (context.threeDSecureRedirectUrl.isNone()) {
        log.error('3D secure redirect url is not set. This is necessary for the registration API in subscription context.')
      }

      if (context.threeDSecureExtraProfile.isNone()) {
        log.error('3D secure extra profile is not set. This is necessary for the registration API in subscription context.')
      }

      const result = await this.creditCardService.create(
        token,
        context.threeDSecureRedirectUrl,
        context.threeDSecureExtraProfile
      )

      return result.fold(
        (r: any) => {

          if (isCreateForSubscriptionError(r, 'kuroneko')) {
            throw errorOnThreeDSecureRegistration({
              errorCode: r.response.body.data.error.errorCode,
              creditErrorCode: r.response.body.data.error.creditErrorCode,
            })
          }

          throw new DefaultThreeDSecureRegistrationError()
        },
        (r: ThreeDSecureCreditcardRegisterResult) => {
          try {
            return {
              name: 'redirect-with-html',
              html: trimCDATA(r.threeDAuthHtml),
            }
          } catch (e) {
            throw new ThreeDAuthHtmlParseError()
          }
        }
      )
    }
  }
}
